import {
  type ColumnDef,
  type ColumnVisibilityState,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table";
import { parseISO } from "date-fns";
import { Columns3, Copy, MoreHorizontal, RotateCw } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { copyToClipboard } from "~/common/functions";
import { type DataTableFeatures, RemoteDataTable } from "~/components/data-table";
import { EmptyState } from "~/components/empty-state";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";

import { useEvidenceList } from "~/features/evidences/hooks";
import type { EvidenceListFilters, EvidenceRow, SortDirection } from "~/features/evidences/types";
import { deriveEvidencePagination, toEvidenceRowList } from "~/features/evidences/utils/transformers";

const SORTABLE: ReadonlySet<string> = new Set(["updatedAt"]);

const EVIDENCE_COLUMNS = [
  { id: "date", labelKey: "ui.date" },
  { id: "name", labelKey: "model.common.name" },
  { id: "reference", labelKey: "model.reference.modelName" },
  { id: "user", labelKey: "model.user.modelName" },
  { id: "updatedAt", labelKey: "model.common.updatedAt" },
] as const;

interface EvidencesTableProps {
  filters: EvidenceListFilters;
  direction: SortDirection;
  onSortingChange: (direction: SortDirection) => void;
  pageIndex: number;
  onPageIndexChange: (next: number) => void;
  pageSize: number;
  onPageSizeChange: (next: number) => void;
  isPending: boolean;
  onSelect: (id: string) => void;
}

const helper = createColumnHelper<DataTableFeatures, EvidenceRow>();

export function EvidencesTable({
  filters,
  direction,
  onSortingChange,
  pageIndex,
  onPageIndexChange,
  pageSize,
  onPageSizeChange,
  isPending,
  onSelect,
}: EvidencesTableProps) {
  const { t } = useTranslation();
  const {
    rows: rawRows,
    rowsLength,
    totalCount,
    refetch,
  } = useEvidenceList({
    filters,
    direction,
    pageIndex,
    pageSize,
  });
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibilityState>({});

  const rows = useMemo(() => toEvidenceRowList(rawRows), [rawRows]);
  const { rowCount, hasNextPage } = deriveEvidencePagination({
    totalCount,
    rowsLength,
    pageIndex,
    pageSize,
  });

  const isVisible = (id: string) => columnVisibility[id] !== false;
  const setVisibility = (id: string, next: boolean) => setColumnVisibility((prev) => ({ ...prev, [id]: next }));
  const toggleAll = (next: boolean) =>
    setColumnVisibility(Object.fromEntries(EVIDENCE_COLUMNS.map((c) => [c.id, next])));

  const sorting: SortingState = useMemo(() => [{ id: "updatedAt", desc: direction === "desc" }], [direction]);

  const handleSortingChange = (next: SortingState) => {
    const first = next[0];
    if (!first || !SORTABLE.has(first.id)) {
      onSortingChange("desc");
      return;
    }
    onSortingChange(first.desc ? "desc" : "asc");
  };

  const handleCopyId = async (id: string) => {
    try {
      await copyToClipboard(id);
      toast.success(t("feature.common.idCopied"));
    } catch {
      toast.error(t("feature.common.copyToClipboardFailed"));
    }
  };

  const columns: ColumnDef<DataTableFeatures, EvidenceRow, any>[] = [
    helper.accessor("date", {
      id: "date",
      header: () => t("ui.date"),
      enableSorting: false,
      cell: (info) => <span className="whitespace-nowrap tabular-nums text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("name", {
      id: "name",
      header: () => t("model.common.name"),
      enableSorting: false,
      cell: (info) => <span className="font-medium text-fg-1">{info.getValue() || "—"}</span>,
    }),
    helper.display({
      id: "reference",
      header: () => t("model.reference.modelName"),
      enableSorting: false,
      cell: (info) => {
        const { referenceShortCode, referenceTitle } = info.row.original;
        if (!referenceShortCode && !referenceTitle) return <span className="text-fg-3">—</span>;
        return (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-default text-fg-2">{referenceShortCode || referenceTitle}</span>
            </TooltipTrigger>
            <TooltipContent>{referenceTitle || referenceShortCode}</TooltipContent>
          </Tooltip>
        );
      },
    }),
    helper.accessor("userName", {
      id: "user",
      header: () => t("model.user.modelName"),
      enableSorting: false,
      cell: (info) => <span className="text-fg-2">{info.getValue() || "—"}</span>,
    }),
    helper.accessor("updatedAt", {
      id: "updatedAt",
      header: () => t("model.common.updatedAt"),
      cell: (info) => {
        const value = info.getValue();
        return (
          <span className="whitespace-nowrap tabular-nums text-fg-2">
            {value ? t("date.long", { date: parseISO(value) }) : "—"}
          </span>
        );
      },
    }),
    helper.display({
      id: "actions",
      header: () => "",
      enableSorting: false,
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex items-center justify-end gap-1.5">
            <Button type="button" variant="outline" size="sm" onClick={() => onSelect(row.id)}>
              {t("ui.view")}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="icon" aria-label={t("ui.operations")}>
                  <MoreHorizontal />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => handleCopyId(row.id)}>
                  <Copy />
                  <span>{t("ui.copyId")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    }),
  ];

  const isEmpty = rows.length === 0 && !isPending;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="outline" size="icon" aria-label={t("ui.columns")}>
              <Columns3 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[200px]">
            <DropdownMenuItem onSelect={() => toggleAll(false)}>{t("ui.hideAll")}</DropdownMenuItem>
            <DropdownMenuItem onSelect={() => toggleAll(true)}>{t("ui.showAll")}</DropdownMenuItem>
            <DropdownMenuSeparator />
            {EVIDENCE_COLUMNS.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={isVisible(col.id)}
                onCheckedChange={(checked) => setVisibility(col.id, checked === true)}
                onSelect={(e) => e.preventDefault()}
              >
                {t(col.labelKey)}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <Button type="button" variant="outline" size="icon" onClick={() => void refetch()} aria-label={t("ui.reload")}>
          <RotateCw />
        </Button>
      </div>

      {isEmpty ? (
        <EmptyState title={t("feature.evidences.empty.title")} description={t("feature.evidences.empty.description")} />
      ) : (
        <RemoteDataTable<EvidenceRow>
          columns={columns}
          data={rows}
          rowCount={rowCount}
          hasNextPage={hasNextPage}
          pageIndex={pageIndex}
          onPageIndexChange={onPageIndexChange}
          pageSize={pageSize}
          onPageSizeChange={onPageSizeChange}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          isPending={isPending}
          emptyMessage={t("feature.common.noData")}
        />
      )}
    </div>
  );
}
